// Helper functions for database operations and schema management
const DB_NAME = 'elearningtbdDb';
const DB_VERSION_KEY = 'dbv';
const DB_VERSION = '4';
// Explicitly export this constant for reuse across the application
export const LAST_SYNC_TIME_KEY = 'lastSyncTime';
const DYNAMIC_TABLES_KEY = 'dynamicTables';
const TABLES_BEING_CREATED = new Set();

// Define known table schemas
const TABLE_SCHEMAS = {
    Invitation: {
        keyPath: 'uuid',  // Using uuid for consistency with other synced tables
        indexes: [
            { name: 'lastModified', keyPath: 'lastModified', unique: false },
            { name: 'isDeleted', keyPath: 'isDeleted', unique: false },
            { name: 'id', keyPath: 'id', unique: true },  // Keep id as a unique index
            { name: 'email', keyPath: 'email', unique: false },
            { name: 'status', keyPath: 'status', unique: false }
        ]
    },
    // Add other table-specific schemas here as needed
};

export const getTableSchema = (tableName) => {
    // First check if we have a specific schema for this table
    if (TABLE_SCHEMAS[tableName]) {
        console.debug(`Using specific schema for table ${tableName}:`, TABLE_SCHEMAS[tableName]);
        return TABLE_SCHEMAS[tableName];
    }

    // Fall back to default schema
    console.debug(`Using default schema for table ${tableName}`);
    return getDefaultTableSchema();
};

export const getDefaultTableSchema = () => ({
    keyPath: 'uuid',
    indexes: [
        { name: 'lastModified', keyPath: 'lastModified', unique: false },
        { name: 'isDeleted', keyPath: 'isDeleted', unique: false }
    ]
});

export const detectSchemaFromRecord = (record) => {
    // Try to detect the key field from the record
    if (record.id && !record.uuid) {
        return {
            keyPath: 'id',
            indexes: [
                { name: 'lastModified', keyPath: 'lastModified', unique: false },
                { name: 'isDeleted', keyPath: 'isDeleted', unique: false }
            ]
        };
    }

    // Default to uuid schema
    return getDefaultTableSchema();
};

export const createObjectStore = (db, tableName, schema = getTableSchema(tableName)) => {
    console.debug(`Creating object store for ${tableName} with schema:`, schema);

    if (!db.objectStoreNames.contains(tableName)) {
        try {
            const store = db.createObjectStore(tableName, { keyPath: schema.keyPath });
            console.debug(`Created store for ${tableName} with keyPath:`, store.keyPath);

            // Always ensure critical indexes exist
            const criticalIndexes = [
                { name: 'uuid', keyPath: 'uuid', unique: true },
                { name: 'lastModified', keyPath: 'lastModified', unique: false },
                { name: 'isDeleted', keyPath: 'isDeleted', unique: false }
            ];

            // Add critical indexes first
            criticalIndexes.forEach(index => {
                if (!store.indexNames.contains(index.name)) {
                    console.debug(`Adding critical index ${index.name} to ${tableName}`);
                    store.createIndex(index.name, index.keyPath, { unique: index.unique });
                }
            });

            // Then add any additional indexes from schema
            if (schema.indexes) {
                schema.indexes.forEach(index => {
                    // Skip if it's one of the critical indexes we already added
                    const isAlreadyAdded = criticalIndexes.some(ci => ci.name === (typeof index === 'string' ? index : index.name));

                    if (!isAlreadyAdded) {
                        if (typeof index === 'string') {
                            if (!store.indexNames.contains(index)) {
                                console.debug(`Adding schema index ${index} to ${tableName}`);
                                store.createIndex(index, index, { unique: false });
                            }
                        } else if (typeof index === 'object') {
                            if (!store.indexNames.contains(index.name)) {
                                console.debug(`Adding schema index ${index.name} to ${tableName}`);
                                store.createIndex(index.name, index.keyPath, { unique: index.unique || false });
                            }
                        }
                    }
                });
            }

            return store;
        } catch (error) {
            console.error(`Error creating store for ${tableName}:`, error);
            throw error;
        }
    }

    const transaction = db.transaction(tableName, 'readwrite');
    const store = transaction.objectStore(tableName);
    console.debug(`Using existing store for ${tableName} with keyPath:`, store.keyPath);
    return store;
};

export const validateRecord = (record, schema) => {
    if (!record[schema.keyPath]) {
        console.error(`Record missing ${schema.keyPath}:`, record);
        throw new Error(`Record missing required key path field: ${schema.keyPath}`);
    }
    return true;
};

export const ensureRecordHasRequiredFields = (record, tableName) => {
    // Get the appropriate schema for this table
    const schema = getTableSchema(tableName);
    console.debug(`Ensuring required fields for ${tableName} with schema:`, schema);

    if (!record[schema.keyPath]) {
        console.error(`Record missing ${schema.keyPath}:`, record);
        throw new Error(`Record missing required key path field: ${schema.keyPath}`);
    }

    // Ensure standard metadata fields exist with proper defaults

    // Ensure lastModified exists
    if (!record.lastModified) {
        record.lastModified = new Date().toISOString();
    }

    // Ensure createdAt exists
    if (!record.createdAt) {
        record.createdAt = new Date().toISOString();
    }

    // Ensure active flag exists with default true
    if (record.active === undefined) {
        record.active = true;
    }

    // Ensure isDeleted flag exists with default false
    if (record.isDeleted === undefined) {
        record.isDeleted = false;
    }

    return record;
};

export const getDynamicTables = () => {
    try {
        return JSON.parse(localStorage.getItem(DYNAMIC_TABLES_KEY) || '{}');
    } catch (error) {
        console.error('Error parsing dynamic tables:', error);
        return {};
    }
};

export const saveDynamicTables = (dynamicTables) => {
    localStorage.setItem(DYNAMIC_TABLES_KEY, JSON.stringify(dynamicTables));
};

export const isTableBeingCreated = (tableName) => {
    return TABLES_BEING_CREATED.has(tableName);
};

export const markTableAsBeingCreated = (tableName) => {
    TABLES_BEING_CREATED.add(tableName);
};

export const unmarkTableAsBeingCreated = (tableName) => {
    TABLES_BEING_CREATED.delete(tableName);
};

export const getCurrentVersion = () => {
    return parseInt(localStorage.getItem(DB_VERSION_KEY) || DB_VERSION);
};

export const incrementVersion = () => {
    const newVersion = getCurrentVersion() + 1;
    localStorage.setItem(DB_VERSION_KEY, newVersion.toString());
    return newVersion;
};

export const createDynamicStores = (db, dynamicTables) => {
    Object.entries(dynamicTables).forEach(([tableName, schema]) => {
        if (!db.objectStoreNames.contains(tableName)) {
            const store = db.createObjectStore(tableName, { keyPath: 'uuid' });

            // Always create the uuid index for lookups - critical for all tables
            if (!store.indexNames.contains('uuid')) {
                store.createIndex('uuid', 'uuid', { unique: true });
            }

            // Add lastModified index for sync tracking if it doesn't exist
            if (!store.indexNames.contains('lastModified')) {
                store.createIndex('lastModified', 'lastModified', { unique: false });
            }

            // Add isDeleted index for tracking deleted records if it doesn't exist
            if (!store.indexNames.contains('isDeleted')) {
                store.createIndex('isDeleted', 'isDeleted', { unique: false });
            }

            // Special case for Notification table - ensure it has userId index
            if (tableName === 'Notification' && !store.indexNames.contains('userId')) {
                // console.log('[dbStorageService] Creating userId index for new Notification table');
                store.createIndex('userId', 'userId', { unique: false });
            }

            // Add any custom indexes that were saved with the schema
            if (schema.indexes) {
                schema.indexes.forEach(index => {
                    if (typeof index === 'string') {
                        if (!store.indexNames.contains(index)) {
                            store.createIndex(index, index, { unique: false });
                        }
                    } else if (typeof index === 'object') {
                        if (!store.indexNames.contains(index.name)) {
                            store.createIndex(index.name, index.keyPath, { unique: index.unique || false });
                        }
                    }
                });
            }

            // Remove from the set of tables being created
            TABLES_BEING_CREATED.delete(tableName);
        }
    });
};

export const preRegisterServerTables = async (db, serverUpdates) => {
    if (!serverUpdates || Object.keys(serverUpdates).length === 0) {
        return false;
    }

    let tablesAdded = false;
    const tablesToCreate = new Set();

    // First validate all records to ensure they have required fields
    for (const [tableName, records] of Object.entries(serverUpdates)) {
        if (!Array.isArray(records) || records.length === 0) continue;

        try {
            // Get the appropriate schema for this table
            const schema = getTableSchema(tableName);

            // Validate first record to check schema
            records.forEach(record => {
                if (!record[schema.keyPath]) {
                    throw new Error(`Record for table ${tableName} missing required key path field: ${schema.keyPath}`);
                }
            });
        } catch (error) {
            console.error(`Validation error for table ${tableName}:`, error);
            throw error; // Re-throw to prevent partial updates
        }

        // If this is a new table we haven't seen before
        if (!db.objectStoreNames.contains(tableName)) {
            tablesToCreate.add(tableName);

            // Add this as a dynamic table with schema information
            const dynamicTables = getDynamicTables();
            dynamicTables[tableName] = {
                created: new Date().toISOString(),
                schema: getTableSchema(tableName),
                indexes: ['isDeleted', 'lastModified'],
                serverDefined: true
            };
            saveDynamicTables(dynamicTables);

            tablesAdded = true;
        }
    }

    return tablesAdded;
};

export const createStores = (db, tablesJson) => {
    const tablesSummary = tablesJson.tablesSummary;
    const tableDetails = tablesJson.tables;

    Object.entries(tablesSummary).forEach(([tableName, config]) => {
        // Special case for SystemConfig which needs sync support despite Sync flag
        const shouldCreateStore = config.Sync === 1 || tableName === 'SystemConfig';

        if (shouldCreateStore && !db.objectStoreNames.contains(tableName)) {
            const store = createObjectStore(db, tableName);

            // Add indexes based on schema
            const tableSchema = tableDetails[tableName];
            tableSchema.forEach(field => {
                if (field.indexed && !store.indexNames.contains(field.name)) {
                    try {
                        store.createIndex(field.name, field.name, {
                            unique: field.indexed === 'unique'
                        });
                        console.debug(`Created index ${field.name} for table ${tableName}`);
                    } catch (error) {
                        console.warn(`Index ${field.name} already exists for table ${tableName}:`, error);
                    }
                }
            });

            // Special case for SystemConfig: add compound index for category and name
            if (tableName === 'SystemConfig' && !store.indexNames.contains('category_name')) {
                try {
                    store.createIndex('category_name', ['category', 'name'], { unique: true });
                    console.debug('Created compound index category_name for SystemConfig');
                } catch (error) {
                    console.warn('category_name index already exists for SystemConfig:', error);
                }
            }
        }
    });
};

export const DB_CONSTANTS = {
    DB_NAME,
    DB_VERSION_KEY,
    DB_VERSION,
    LAST_SYNC_TIME_KEY,
    DYNAMIC_TABLES_KEY
};

/**
 * Safely converts a value to a valid Date object for IndexedDB operations
 * @param {any} timestamp - The timestamp value to validate (Date, string, number)
 * @param {boolean} enableLogging - Whether to log validation issues
 * @param {string} context - Context information for logging
 * @returns {Date} A valid Date object or fallback to epoch
 */
export const ensureValidTimestamp = (timestamp, enableLogging = true, context = '') => {
    let validDate;

    try {
        // Case 1: null/undefined - use epoch
        if (timestamp === null || timestamp === undefined) {
            if (enableLogging) console.warn(`${context}: Null/undefined timestamp, using epoch`);
            return new Date(0);
        }

        // Case 2: Already a Date object
        if (timestamp instanceof Date) {
            validDate = timestamp;
        }
        // Case 3: ISO string format
        else if (typeof timestamp === 'string') {
            validDate = new Date(timestamp);
        }
        // Case 4: Numeric timestamp (milliseconds)
        else if (typeof timestamp === 'number' && !isNaN(timestamp)) {
            validDate = new Date(timestamp);
        }
        // Case 5: Anything else - use epoch
        else {
            if (enableLogging) console.warn(`${context}: Unsupported timestamp type ${typeof timestamp}, using epoch`);
            return new Date(0);
        }

        // Check if resulting Date is valid
        if (isNaN(validDate.getTime())) {
            if (enableLogging) console.warn(`${context}: Invalid date value produced: ${timestamp}, using epoch`);
            return new Date(0);
        }

        return validDate;
    } catch (error) {
        if (enableLogging) console.error(`${context}: Error validating timestamp:`, error);
        return new Date(0); // Safe fallback
    }
};

/**
 * Normalizes any timestamp format to ISO string for consistent comparison
 * @param {any} timestamp - The timestamp to normalize (Date, string, number)
 * @returns {string} - Normalized ISO string timestamp
 */
export const normalizeTimestamp = (timestamp) => {
    if (!timestamp) return new Date().toISOString();

    if (timestamp instanceof Date) {
        return timestamp.toISOString();
    }

    // If it's already an ISO string, return as is
    if (typeof timestamp === 'string' && timestamp.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
        return timestamp;
    }

    // Otherwise, create a new Date and convert to ISO
    return new Date(timestamp).toISOString();
}; 